import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Spin, Tooltip } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import ConnectedAssigneeSelect from '../task-components/ConnectedAssigneeSelect';
import CalendarDropdown from '../task-components/CalendarDropdown';

const AddingProgressLoader = () => (
  <div
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(255,255,255,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
    }}
  >
    <Spin size="small" />
  </div>
);

const DEFAULT_STOP_PROPAGATION_FN = (e) => e.stopPropagation();

const AddSubTask = ({ compressed, handleSubmit, onClose }) => {
  const intl = useIntl();
  const inputRef = useRef();

  const [taskName, setTaskName] = useState('');
  const [taskAssignee, setTaskAssignee] = useState([]);
  const [taskDate, setTaskDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const onSave = useCallback(async (taskAssignee, taskDate, taskName) => {
    if (!taskName?.trim() || isLoading) return;

    setIsLoading(true);

    await handleSubmit({
      taskAssignee,
      taskDate,
      taskName,
    });
    setTaskName('');
    setTaskAssignee([]);
    setTaskDate('');
    setIsLoading(false);
  }, [handleSubmit, isLoading]);

  const handleKeyDown = useCallback(
    (e) => {
      if ((e?.key === 'Enter' && !e?.shiftKey) || e?.key === 'Escape') {
        e.preventDefault();
        onSave(taskAssignee, taskDate, taskName);
      }
    },
    [onSave, taskAssignee, taskDate, taskName],
  );

  const handleChange = useCallback((e) => {
    setTaskName(e?.target?.value);
  }, []);

  const handleDateChange = useCallback((date) => {
    setTaskDate(date);
    onSave(taskAssignee, date, taskName);
  }, [onSave, taskAssignee, taskName]);

  const handleAssigneeChange = useCallback((assignees) => {
    setTaskAssignee(assignees);
    onSave(assignees, taskDate, taskName);
  }, [onSave, taskDate, taskName]);

  const handleAssigneeSet = useCallback((assignees) => {
    setTaskAssignee(assignees);
  }, []);

  const handleClickAway = useCallback(() => {
    onSave(taskAssignee, taskDate, taskName);
    onClose?.();
  }, [onClose, onSave, taskAssignee, taskDate, taskName]);

  const calendarValue = useMemo(() => ({ dueDate: taskDate }), [taskDate]);

  const handleTextAreaStretch = useCallback(() => {
    if (!inputRef?.current) {
      return;
    }
    inputRef.current.style.height = 0;
    inputRef.current.style.height = `${inputRef?.current?.scrollHeight + 2}px`;
  }, []);

  useEffect(() => {
    setTaskAssignee([]);
    setTaskDate('');
    setTaskName('');
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (!isLoading && inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading]);

  useEffect(() => {
    handleTextAreaStretch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskName])

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div
        className="add-subtask-wrapper relative flex justify-between w-full mb-3 boder-slate-600 align-start"
      >
        {isLoading && <AddingProgressLoader />}
        <textarea
          className={`subtask-add-textarea ${compressed ? 'text-xs' : 'text-sm'} cursor-text `}
          ref={inputRef}
          name="taskTitle"
          placeholder={intl.formatMessage({ id: 'ENTER_SUBRASK_TITLE', defaultMessage: 'Enter subtask title' })}
          value={taskName}
          onChange={handleChange}
          required
          autoFocus
          type="text"
          disabled={isLoading}
        />
        <div className="add-subtask-right-side" onClick={DEFAULT_STOP_PROPAGATION_FN}>
          <div className="flex align-center justify-end">
            <div className="mr-2">
              <ConnectedAssigneeSelect
                iconSize={24}
                count={1}
                callback={handleAssigneeChange}
                setCurrentValue={handleAssigneeSet}
                currentValue={taskAssignee}
                disabled={isLoading}
              />
            </div>
            <Tooltip title="Set date">
              <div className="w-16">
                <CalendarDropdown
                  showIcon={false}
                  onClick={handleDateChange}
                  format="MMM DD"
                  task={calendarValue}
                  disabled={isLoading}
                />
              </div>
            </Tooltip>
          </div>
        </div>
        <div className="show-subtask-details-btn" />
      </div>
    </ClickAwayListener>
  );
};

export default AddSubTask;
